"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  Loader2,
  AlertCircle,
  X,
  ChevronDown,
  Mic,
  Video,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { MorphingText } from "@/components/ui/gooey-text-morphing";
import VideosSection from "@/components/content/sections/videos-section";
import { ComboBoxResponsive } from "@/components/ui/combo-box-responsive";
import { useI18n } from "@/lib/i18n";

interface PodcastResult {
  id: string;
  title: string;
  author: string;
  description?: string;
  imageUrl?: string;
  categories?: string[];
}

interface VideoResult {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  videoId: string;
  thumbnail: string;
  duration?: number;
  durationFormatted?: string;
  viewCount?: number;
  viewCountText?: string;
  publishedTimeText?: string;
  publishDate?: string;
  description?: string;
}

interface Channel {
  value: string;
  label: string;
  id: string;
}

// Common podcast categories
const PODCAST_CATEGORIES = [
  "Arts",
  "Business",
  "Comedy",
  "Education",
  "Fiction",
  "Government",
  "Health & Fitness",
  "History",
  "Kids & Family",
  "Leisure",
  "Music",
  "News",
  "Religion & Spirituality",
  "Science",
  "Society & Culture",
  "Sports",
  "Technology",
  "True Crime",
  "TV & Film",
];

export default function SearchPage() {
  const { t } = useI18n();
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [results, setResults] = useState<PodcastResult[]>([]);
  const [videos, setVideos] = useState<VideoResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [videosPage, setVideosPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [videosHasMore, setVideosHasMore] = useState(false);
  const [showingTrending, setShowingTrending] = useState(true);
  const [searchMode, setSearchMode] = useState<"podcast" | "video">("podcast");
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Refs for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  // Fetch trending videos from YouTube
  const fetchTrendingVideos = async (loadMore = false, targetChannel?: Channel | null) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setVideosPage(1);
      setVideos([]); // Clear existing videos when starting fresh
    }
    setError("");
    setShowingTrending(true);

    try {
      const requestPage = loadMore ? videosPage + 1 : 1;
      const params = new URLSearchParams({
        limit: "30",
        page: requestPage.toString(),
        geo: "US",
      });

      console.log(
        `Fetching trending videos: page=${requestPage}, loadMore=${loadMore}`
      );

      let response, data;
      
      const currentChannel = targetChannel !== undefined ? targetChannel : selectedChannel;
      
      if (currentChannel && currentChannel.id) {
        // Use channel-specific API when a channel is selected
        const channelParams = new URLSearchParams({
          channelId: currentChannel.id,
          limit: "30",
        });
        response = await fetch(`/api/youtube-channel-videos?${channelParams}`);
        data = await response.json();
      } else {
        // Use trending API when no channel is selected
        response = await fetch(`/api/youtube-trending?${params}`);
        data = await response.json();
      }

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch trending videos");
      }

      if (loadMore) {
        setVideos((prevVideos) => [...prevVideos, ...data.videos]);
        setVideosPage(requestPage);
      } else {
        setVideos(data.videos || []);
        setVideosPage(1);
      }
      setTotalCount(data.totalCount || 0);
      setVideosHasMore(data.hasMore || false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load trending videos"
      );
      if (!loadMore) {
        setVideos([]);
        setTotalCount(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Fetch trending podcasts
  const fetchTrendingPodcasts = async (loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setPage(1);
      setResults([]); // Clear existing results when starting fresh
    }
    setError("");
    setShowingTrending(true);

    try {
      // Calculate the correct page number for the request
      const requestPage = loadMore ? page + 1 : 1;
      const params = new URLSearchParams({
        limit: "30",
        page: requestPage.toString(),
      });

      if (selectedCategory) {
        params.append("category", selectedCategory);
      }

      console.log(
        `Fetching trending podcasts: page=${requestPage}, loadMore=${loadMore}`
      );

      const response = await fetch(`/api/trending-podcasts?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch trending podcasts");
      }

      // console.log(`Trending API response:`, {
      //   podcastsCount: data.podcasts?.length,
      //   hasMore: data.hasMore,
      //   totalCount: data.totalCount
      // });

      if (loadMore) {
        setResults((prevResults) => [...prevResults, ...data.podcasts]);
        setPage(requestPage);
      } else {
        setResults(data.podcasts || []);
        setPage(1);
      }
      setTotalCount(data.totalCount || 0);
      setHasMore(data.hasMore || false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load trending podcasts"
      );
      if (!loadMore) {
        setResults([]);
        setTotalCount(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleSearch = async (loadMore = false) => {
    // For podcasts: Allow search if either query or category is selected
    // For videos: Only allow search if query is provided
    if (searchMode === "podcast" && !query.trim() && !selectedCategory) return;
    if (searchMode === "video" && !query.trim()) return;

    // Switch from trending to search results
    setShowingTrending(false);

    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      if (searchMode === "podcast") {
        setPage(1);
        setResults([]);
      } else {
        setVideosPage(1);
        setVideos([]);
      }
    }
    setError("");

    try {
      const params = new URLSearchParams({
        limit: "30",
        page: loadMore ? 
          (searchMode === "podcast" ? (page + 1).toString() : (videosPage + 1).toString()) : 
          "1",
      });

      // Add query only if it's not empty
      if (query.trim()) {
        params.append("q", query);
      }

      // Add category only for podcast search
      if (searchMode === "podcast" && selectedCategory) {
        params.append("category", selectedCategory);
      }

      // For video mode, check if we need to search within a specific channel
      let apiEndpoint;
      if (searchMode === "podcast") {
        apiEndpoint = "/api/search/podcasts";
      } else {
        // Video mode: if a specific channel is selected, use channel search
        if (selectedChannel && selectedChannel.id) {
          apiEndpoint = "/api/youtube-channel-videos";
          params.append("channelId", selectedChannel.id);
          // Remove the page parameter as channel API uses different pagination
          params.delete("page");
        } else {
          apiEndpoint = "/api/search/videos";
        }
      }

      const response = await fetch(`${apiEndpoint}?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Search failed");
      }

      if (searchMode === "podcast") {
        if (loadMore) {
          setResults([...results, ...data.podcasts]);
          setPage(page + 1);
        } else {
          setResults(data.podcasts || []);
        }
        setTotalCount(data.totalCount || 0);
        setHasMore(data.hasMore || false);
      } else {
        if (loadMore) {
          setVideos([...videos, ...data.videos]);
          setVideosPage(videosPage + 1);
        } else {
          setVideos(data.videos || []);
        }
        setTotalCount(data.totalCount || 0);
        setVideosHasMore(data.hasMore || false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      if (!loadMore) {
        if (searchMode === "podcast") {
          setResults([]);
        } else {
          setVideos([]);
        }
        setTotalCount(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const clearQuery = () => {
    setQuery("");
    if (!selectedCategory) {
      setShowingTrending(true);
      fetchTrendingPodcasts();
    }
  };

  const clearCategory = () => {
    setSelectedCategory("");
    setResults([]);
    setTotalCount(0);
    setError("");
    setPage(1);
    setHasMore(false);
    // Return to trending when clearing filters
    if (!query.trim()) {
      setShowingTrending(true);
      fetchTrendingPodcasts();
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Auto-search when category is selected (even without query)
    if (category) {
      setTimeout(() => {
        // Reset results and search with new category
        setResults([]);
        setPage(1);
        if (query.trim()) {
          handleSearch();
        } else {
          // If no search query, show trending for this category
          fetchTrendingPodcasts();
        }
      }, 100);
    } else if (!query.trim()) {
      // If clearing category and no query, return to trending
      setTimeout(() => {
        fetchTrendingPodcasts();
      }, 100);
    }
  };

  // const hasActiveFilters = selectedCategory;

  // Handle podcast click
  const handlePodcastClick = (podcast: PodcastResult) => {
    router.push(`/podcast/${podcast.id}`);
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    if (searchMode === "podcast") {
      if (hasMore && !isLoadingMore && !isLoading && results.length > 0) {
        if (showingTrending) {
          fetchTrendingPodcasts(true);
        } else {
          handleSearch(true);
        }
      }
    } else if (searchMode === "video") {
      if (videosHasMore && !isLoadingMore && !isLoading && videos.length > 0) {
        fetchTrendingVideos(true);
      }
    }
  }, [hasMore, videosHasMore, isLoadingMore, isLoading, results.length, videos.length, showingTrending, searchMode]);

  // Load trending content on page load
  useEffect(() => {
    // Clear data when switching modes
    setResults([]);
    setVideos([]);
    setSelectedChannel(null);
    setError("");
    
    if (searchMode === "podcast") {
      fetchTrendingPodcasts();
    } else if (searchMode === "video") {
      fetchTrendingVideos();
    }
  }, [searchMode]); // Run when search mode changes

  // Keyboard shortcut: focus search with '/'; ESC clears when focused
  useEffect(() => {
    const handleGlobalKeydown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        (e.target as HTMLElement)?.isContentEditable;
      if (!isTyping && e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleGlobalKeydown);
    return () => window.removeEventListener("keydown", handleGlobalKeydown);
  }, []);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "100px", // Trigger 100px before reaching the element
        threshold: 0.1,
      }
    );

    // Observe the trigger element
    const triggerElement = loadMoreTriggerRef.current;
    if (triggerElement && observerRef.current) {
      observerRef.current.observe(triggerElement);
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMore]);

  // const StaticSvgText = ({ text, className = "" }) => (
  //   <div
  //     className={`relative h-16 text-center font-sans text-[40pt] font-bold leading-none [filter:url(#threshold)_blur(0.6px)] md:h-24 lg:text-[6rem] ${className}`}
  //   >
  //     <span className="inline-block w-full">{text}</span>
  //   </div>
  // );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Hero Section with Search */}
      <div className="relative pt-16 pb-12">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          {/* Header */}
          <div className="flex flex-col items-center">
            <div className="flex flex-row items-center justify-center mb-4">
              <MorphingText
                staticText="Discover"
                texts={["Podcasts", "YouTube"]}
              />
            </div>
          </div>

          {/* Modern Search Interface */}
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Search Mode Toggle and Main Search Bar */}
              <div className="flex items-center gap-4">
                {/* Search Mode Toggle */}
                <div className="relative bg-background border border-border rounded-2xl p-1 flex shadow-lg">
                  <div
                    className="absolute inset-1 rounded-xl bg-primary shadow-md transition-all duration-300 ease-in-out"
                    style={{
                      width: "calc(50% - 4px)",
                      left: searchMode === "podcast" ? "4px" : "calc(50%)",
                    }}
                  />
                  <button
                    type="button"
                    className={`relative z-10 px-4 py-2 flex items-center justify-center transition-colors duration-300 rounded-xl ${
                      searchMode === "podcast"
                        ? "text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => setSearchMode("podcast")}
                  >
                    <Mic className="w-4 h-4" strokeWidth={2} />
                    <span className="text-sm font-medium ml-2">{t('search.podcast')}</span>
                  </button>
                  <button
                    type="button"
                    className={`relative z-10 px-4 py-2 flex items-center justify-center transition-colors duration-300 rounded-xl ${
                      searchMode === "video"
                        ? "text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => setSearchMode("video")}
                  >
                    <Video className="w-4 h-4" strokeWidth={2} />
                    <span className="text-sm font-medium ml-2">{t('search.video')}</span>
                  </button>
                </div>

                {/* Main Search Bar */}
                <div className="relative group flex-1">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-primary/60 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
                  <div className="relative flex items-center bg-background border border-border rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex-1 relative">
                      <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                      <input
                        type="text"
                        placeholder={searchMode === "podcast" 
                          ? "Search podcasts (e.g., Tech News, Lex Fridman)..." 
                          : "Search videos (e.g., Music, Gaming, Tech)..."
                        }
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        ref={searchInputRef}
                        aria-label={searchMode === "podcast" ? "Search podcasts" : "Search videos"}
                        onKeyDown={(e) => {
                          if (e.key === "Escape" && query) {
                            e.stopPropagation();
                            clearQuery();
                          }
                        }}
                        className="w-full pl-14 pr-12 py-2.5 text-base bg-transparent border-none focus:outline-none placeholder:text-muted-foreground/60"
                        disabled={isLoading}
                      />
                      {/* Right-side inline controls inside input area */}
                      <div className="absolute inset-y-0 right-3 flex items-center gap-2">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : query ? (
                          <button
                            type="button"
                            onClick={clearQuery}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Clear search"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="hidden sm:inline-flex items-center justify-center rounded-md border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            /
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category/Channel Dropdown */}
                    <div className="hidden sm:block border-l border-border/30 pl-6 pr-2">
                      {searchMode === "podcast" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-2 px-4 py-2.5 bg-transparent border-none focus:outline-none cursor-pointer hover:text-foreground transition-colors min-w-[140px] justify-between">
                              <span className="truncate">
                                {selectedCategory || "All"}
                              </span>
                              <ChevronDown className="h-4 w-4 flex-shrink-0" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => handleCategoryChange("")}
                              className={
                                selectedCategory === "" ? "bg-muted" : ""
                              }
                            >
                              {t('search.allCategories')}
                            </DropdownMenuItem>
                            {PODCAST_CATEGORIES.map((category) => (
                              <DropdownMenuItem
                                key={category}
                                onClick={() => handleCategoryChange(category)}
                                className={
                                  selectedCategory === category ? "bg-muted" : ""
                                }
                              >
                                {t(`search.categories.${category}`)}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <ComboBoxResponsive
                          selectedChannel={selectedChannel}
                          onChannelChange={(channel) => {
                            setSelectedChannel(channel);
                            // Immediately fetch with the new channel
                            fetchTrendingVideos(false, channel);
                          }}
                        />
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="p-1">
                      <button
                        type="submit"
                        disabled={
                          isLoading || 
                          (searchMode === "podcast" && (!query.trim() && !selectedCategory)) ||
                          (searchMode === "video" && !query.trim())
                        }
                        className="px-6 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('search.search')
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Category/Channel Selector */}
              <div className="sm:hidden">
                {searchMode === "podcast" ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="w-full flex items-center justify-between px-6 py-3 bg-background border border-border/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-muted-foreground hover:text-foreground transition-colors">
                              <span className="truncate">
                                {selectedCategory ? t(`search.categories.${selectedCategory}`) : t('search.trending')}
                              </span>
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-[var(--radix-dropdown-menu-trigger-width)]"
                      align="center"
                    >
                      <DropdownMenuItem
                        onClick={() => handleCategoryChange("")}
                        className={selectedCategory === "" ? "bg-muted" : ""}
                      >
                        {t('search.allCategories')}
                      </DropdownMenuItem>
                      {PODCAST_CATEGORIES.map((category) => (
                        <DropdownMenuItem
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                          className={
                            selectedCategory === category ? "bg-muted" : ""
                          }
                        >
                          {t(`search.categories.${category}`)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="w-full">
                    <ComboBoxResponsive
                      selectedChannel={selectedChannel}
                      onChannelChange={(channel) => {
                        setSelectedChannel(channel);
                        // Immediately fetch with the new channel
                        fetchTrendingVideos(false, channel);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Active Filter Tags */}
              {selectedCategory && (
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm border border-primary/20">
                    <span>{t('search.categoryPrefix')} {t(`search.categories.${selectedCategory}`)}</span>
                    <button
                      type="button"
                      onClick={clearCategory}
                      className="hover:bg-primary/20 rounded-full p-1 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Search Results Section */}
      <div className="container mx-auto px-4 max-w-7xl pb-16">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="relative mb-4 flex justify-center">
                <SpinnerIcon size={32} color="hsl(var(--primary))" />
              </div>
              <p className="text-muted-foreground text-lg">
                {searchMode === "podcast" ? t('search.searchingPodcasts') : t('search.loadingVideos')}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="p-6 bg-destructive/10 border border-destructive/20 text-destructive rounded-2xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium mb-1">{t('search.errorTitle')}</h3>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !error && 
         ((searchMode === "podcast" && results.length === 0) || 
          (searchMode === "video" && videos.length === 0)) && 
         !showingTrending && (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-medium mb-2">{t('search.noResults')}</h3>
              <p className="text-muted-foreground">
                {searchMode === "podcast" 
                  ? t('search.tryAdjustPodcasts')
                  : t('search.tryAdjustVideos')
                }
              </p>
            </div>
          </div>
        )}

        {/* Video Results */}
        {searchMode === "video" && videos.length > 0 && (
          <VideosSection
            videos={videos}
            isLoading={isLoading}
            onLoadMore={handleLoadMore}
            hasMore={videosHasMore}
            isLoadingMore={isLoadingMore}
            title={t('search.trendingVideos')}
          />
        )}

        {/* Podcast Results */}
        {searchMode === "podcast" && results.length > 0 && (
          <div className="space-y-8">
            {/* Grid Layout */}
            <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
              {results.map((podcast) => (
                <div
                  key={podcast.id}
                  className="group cursor-pointer"
                  onClick={() => handlePodcastClick(podcast)}
                >
                  <div className="space-y-3">
                    {/* Podcast Cover Image */}
                    <div className="relative aspect-square overflow-hidden rounded-xl bg-muted shadow-md transition-all duration-300 group-hover:shadow-xl group-hover:scale-105">
                      {podcast.imageUrl ? (
                        <img
                          src={podcast.imageUrl}
                          alt={podcast.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement("div");
                              fallback.className =
                                "flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/10";
                              fallback.innerHTML = `
                                <svg class="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-primary/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                              `;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/10">
                          <svg
                            className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-primary/30"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                            />
                          </svg>
                        </div>
                      )}

                      {/* Enhanced Hover Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-all duration-300 group-hover:opacity-100">
                        <div className="transform scale-0 group-hover:scale-100 transition-transform duration-300">
                          <div className="rounded-full bg-white/95 backdrop-blur-sm p-3 sm:p-4 text-black shadow-xl">
                            <svg
                              className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Podcast Info */}
                    <div className="space-y-2 px-1">
                      <h3 className="text-xs sm:text-sm font-semibold leading-tight line-clamp-1 group-hover:text-primary transition-colors">
                        {podcast.title}
                      </h3>
                      <div className="flex flex-col gap-1">
                        {podcast.categories && podcast.categories[0] ? (
                          <Badge
                            variant="secondary"
                            className="text-[10px] font-thin sm:text-xs px-2 py-0.5 w-fit rounded-full text-foreground/50 border-primary/20 hover:bg-primary/20"
                          >
                            {t(`search.categories.${podcast.categories[0]}`)}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Section - For both search results and trending */}
            {results.length > 0 && (
              <div className="pt-8">
                {/* Invisible trigger element for intersection observer */}
                {hasMore && !isLoadingMore && (
                  <div
                    ref={loadMoreTriggerRef}
                    className="h-1"
                    aria-hidden="true"
                  />
                )}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-muted-foreground">{t('search.loadingMorePodcasts')}</p>
                    </div>
                  </div>
                )}

                {/* End of results message */}
                {!hasMore && results.length > 0 && (
                  <div className="text-center py-12">
                    <div className="max-w-md mx-auto">
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="h-6 w-6 text-muted-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-muted-foreground">{t('search.endOfResults')}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
